import {
  VStack,
  Stack,
  Text,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Link,
  Icon,
  Spinner,
  Center,
  HStack,
  Badge,
  useToast,
} from "@chakra-ui/react";
import { KYDInvoice } from "@common/interfaces/KYDBilling";
import { FiDownload } from "react-icons/fi";

const creditFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

interface InvoicesSectionProps {
  invoices: KYDInvoice[];
  isLoading?: boolean;
}

export const InvoicesSection: React.FC<InvoicesSectionProps> = ({
  invoices,
  isLoading = false,
}) => {
  const toast = useToast();

  const copyInvoiceId = async (invoiceId: string) => {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      toast({
        title: "Clipboard unavailable",
        description: "Unable to copy invoice ID in this environment.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(invoiceId);
      toast({
        title: "Invoice ID copied",
        description: invoiceId,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch {
      toast({
        title: "Unable to copy invoice ID",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatBillingPeriod = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const startMonth = start.toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
    });
    const endMonth = end.toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return `${startMonth}–${endMonth}`;
  };

  const formatCredits = (total: number) => {
    return creditFormatter.format(total);
  };

  const formatAmount = (amount: number, currency: string = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Assuming amount is in cents
  };

  // Show loading spinner when initially loading
  if (isLoading) {
    return (
      <VStack w="100%" spacing={6}>
        <Stack w="100%" spacing={{ base: "8", lg: "6" }}>
          {/* Header */}
          <Stack
            spacing="4"
            direction={{ base: "column", lg: "row" }}
            w="full"
            justify="space-between"
            align={{ base: "start", lg: "center" }}
          >
            <Stack spacing="1">
              <Text fontSize="lg" fontWeight={"medium"}>
                Billing Statements
              </Text>
            </Stack>
          </Stack>

          {/* Loading State */}
          <Box
            bg="gray.50"
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            shadow="sm"
            w="full"
          >
            <Center py={8} w="full">
              <Stack spacing={4} align="center" w="full">
                <Spinner size="lg" color="blue.500" />
                <Text fontSize="sm" color="gray.500">
                  Loading billing statements...
                </Text>
              </Stack>
            </Center>
          </Box>
        </Stack>
      </VStack>
    );
  }

  return (
    <VStack w="100%" spacing={6}>
      <Stack w="100%" spacing={{ base: "8", lg: "6" }}>
        {/* Header */}
        <Stack
          spacing="4"
          direction={{ base: "column", lg: "row" }}
          justify="space-between"
          align={{ base: "start", lg: "center" }}
        >
          <Stack spacing="1">
            <Text fontSize="lg" fontWeight={"medium"}>
              Billing Statements
            </Text>
          </Stack>
        </Stack>

        {/* Invoices Table */}
        <Box
          bg="white"
          p={3}
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          shadow="sm"
          w="full"
        >
          {invoices.length === 0 ? (
            <Center py={8}>
              <Text fontSize="sm" color="gray.500">
                No billing statements available
              </Text>
            </Center>
          ) : (
            <Box overflowX={{ base: "auto", lg: "visible" }} w="full">
              <Table variant="unstyled" size="sm" w="full">
                <Thead>
                  <Tr>
                    <Th
                      fontSize="xs"
                      color="gray.600"
                      fontWeight="bold"
                      textTransform="uppercase"
                      py={2}
                      px={2}
                      whiteSpace={{ base: "nowrap", lg: "normal" }}
                    >
                      Billing Period
                    </Th>
                    <Th
                      fontSize="xs"
                      color="gray.600"
                      fontWeight="bold"
                      textTransform="uppercase"
                      py={2}
                      px={2}
                      whiteSpace={{ base: "nowrap", lg: "normal" }}
                    >
                      Status
                    </Th>
                    <Th
                      fontSize="xs"
                      color="gray.600"
                      fontWeight="bold"
                      textTransform="uppercase"
                      py={2}
                      px={2}
                      whiteSpace={{ base: "nowrap", lg: "normal" }}
                    >
                      Payment Date
                    </Th>
                    <Th
                      fontSize="xs"
                      color="gray.600"
                      fontWeight="bold"
                      textTransform="uppercase"
                      py={2}
                      px={2}
                      whiteSpace={{ base: "nowrap", lg: "normal" }}
                    >
                      Credits Used
                    </Th>
                    <Th
                      fontSize="xs"
                      color="gray.600"
                      fontWeight="bold"
                      textTransform="uppercase"
                      py={2}
                      px={2}
                      whiteSpace={{ base: "nowrap", lg: "normal" }}
                    >
                      Amount
                    </Th>
                    {/* <Th
                      py={2}
                      px={2}
                      whiteSpace={{ base: "nowrap", lg: "normal" }}
                    ></Th> */}
                  </Tr>
                </Thead>
                <Tbody>
                  {invoices.map((invoice) => (
                    <Tr key={invoice.id}>
                      <Td
                        fontSize="sm"
                        color="gray.700"
                        py={2}
                        px={2}
                        whiteSpace={{ base: "nowrap", lg: "normal" }}
                      >
                        {formatBillingPeriod(
                          invoice.period_start,
                          invoice.period_end
                        )}
                      </Td>
                      <Td
                        fontSize="sm"
                        color="gray.700"
                        py={2}
                        px={2}
                        whiteSpace={{ base: "nowrap", lg: "normal" }}
                      >
                        <Badge
                          colorScheme={
                            invoice.status === "PAID"
                              ? "green"
                              : invoice.status === "PENDING"
                              ? "yellow"
                              : "red"
                          }
                          onDoubleClick={() => void copyInvoiceId(invoice.id)}
                          title="Double-click to copy invoice ID"
                        >
                          {invoice.status}
                        </Badge>
                      </Td>
                      <Td
                        fontSize="sm"
                        color="gray.700"
                        py={2}
                        px={2}
                        whiteSpace={{ base: "nowrap", lg: "normal" }}
                      >
                        {invoice.paid_at ? formatDate(invoice.paid_at) : "-"}
                      </Td>
                      <Td
                        fontSize="sm"
                        color="gray.700"
                        py={2}
                        px={2}
                        whiteSpace={{ base: "nowrap", lg: "normal" }}
                      >
                        {formatCredits(
                          invoice.line_items?.reduce(
                            (acc, item) => acc + item.usage_quantity,
                            0
                          ) || 0
                        )}
                      </Td>
                      <Td
                        fontSize="sm"
                        color="gray.700"
                        fontWeight="medium"
                        py={2}
                        px={2}
                        whiteSpace={{ base: "nowrap", lg: "normal" }}
                      >
                        {formatAmount(invoice.total, invoice.currency)}
                      </Td>
                      {/* <Td
                        py={2}
                        px={2}
                        whiteSpace={{ base: "nowrap", lg: "normal" }}
                      >
                        <Link
                          color="blue.500"
                          fontSize="sm"
                          textDecoration="underline"
                          _hover={{ color: "blue.600" }}
                          onClick={() => {
                            // TODO: Implement PDF download functionality
                            console.log(
                              "Download PDF for invoice:",
                              invoice.id
                            );
                          }}
                        >
                          <HStack spacing={1}>
                            <Icon as={FiDownload} />
                            <Text>PDF</Text>
                          </HStack>
                        </Link>
                      </Td> */}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      </Stack>
    </VStack>
  );
};
